import { IdentityDocumentTypeQuery } from '@dreryk/drerykweb-medical-dictionaries-api';
import {
   EntitlementStatementLegalBasisQuery,
   LegalBasis,
   Patient,
   PatientQuery,
} from '@dreryk/egabinet-patient-frontend-api';
import { IdentityDocumentType } from '@dreryk/egabinet-patient-published-language-api';

import { useCqrsFind, useCqrsSearch } from '@dreryk/command-query-react-common';
import { Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { PatientLoader } from '../../../common/PatientLoader';
import { entitlementStatementsTranslationKeys } from '../../../translations';

type EntitlementStatementCreateFormLoaderRenderProps = {
   patient: Patient | null;
   legalBasis: LegalBasis[];
   identityDocumentType: IdentityDocumentType[];
};

type EntitlementStatementCreateFormLoaderProps = {
   readonly patientId: number;
   readonly children: (renderProps: EntitlementStatementCreateFormLoaderRenderProps) => JSX.Element;
};

export const EntitlementStatementCreateFormLoader = ({
   patientId,
   children,
}: EntitlementStatementCreateFormLoaderProps): JSX.Element => {
   const { data: patient, loading: patientLoading, error: patientError } = useCqrsFind<Patient>(() => {
      const query = new PatientQuery();
      query.id = Slot.of(Number(patientId));
      return query;
   });

   const { data: legalBasis, loading: legalBasisLoading, error: legalBasisError } = useCqrsSearch<LegalBasis>(() => {
      const query = new EntitlementStatementLegalBasisQuery();
      query.entitlementStatementType = 'FOR_BENEFICIARY';
      return query;
   });

   const {
      data: identityDocumentType,
      loading: identityDocumentTypeLoading,
      error: identityDocumentTypeError,
   } = useCqrsSearch<IdentityDocumentType>(() => new IdentityDocumentTypeQuery());

   if (patientLoading || legalBasisLoading || identityDocumentTypeLoading) {
      return <PatientLoader />;
   }

   if (patientError || legalBasisError || identityDocumentTypeError) {
      return (
         <Message
            testId={'entitlementStatementLoaderError'}
            type={MessageType.NEGATIVE}
            label={entitlementStatementsTranslationKeys.createModal.errors.loadData}
            displayIndicator
         />
      );
   }

   return children({
      patient: patient,
      legalBasis: legalBasis,
      identityDocumentType: identityDocumentType,
   });
};
